/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ReportService } from '../../../services';
import { REPORT_JOB_LENDER_S_SCHEDULE } from '../../../';
import { JobSalesPerson } from '../job.model';
import { CommunicationListItem } from '../../../communications/resources/communications.model';
import {
  CommunicationRestService,
  ContactRestService,
  InvoiceType,
  JobChangeOrderRestService,
  JobCommunicationRestService,
  JobDatesRestService,
  JobDrawsRestService,
  JobEDocRestService,
  JobGeneralInfoRestService,
  JobInsuranceRestService,
  JobLetterDocRestService,
  JobRestService,
  JobRfqRestService,
  JobSharedDocRestService,
  JobStageRestService,
  JobWarrantyRestService,
  LetterRestService,
  reportIdField,
  SystemRoleBit,
  systemRolesBitSet,
  UserRestService,
} from '@gh/builder-shared-data';
import { DictionaryService } from '@gh/common-shared';
import { EntityVersion, ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  JobBasicInfo,
  JobDrawsDetails,
  jobDrawsDetailsFromDto,
  JobDrawsRecalculateRequest,
} from './job-details-tab-model';

function getParentJobId(route: ActivatedRouteSnapshot): ID {
  if (route.parent) {
    return route.parent.params['jobId'];
  } else {
    throw new Error('JobDetailsTabBusinessService: route parent is not defined');
  }
}

@Injectable()
export class JobDrawDetailsTabBusinessService {
  jobId: ID;

  constructor(private generalTabRestService: JobGeneralInfoRestService,
              private jobWarrantyRestService: JobWarrantyRestService,
              private gridLayoutService: GridLayoutService,
              private contactRestService: ContactRestService,
              private dictionaryService: DictionaryService,
              private jobStageRestService: JobStageRestService,
              private jobRest: JobRestService,
              private jobDatesInfoRestService: JobDatesRestService,
              private userRestService: UserRestService,
              private jobRfqRestService: JobRfqRestService,
              private lettersRestService: JobLetterDocRestService,
              private jobEDocRest: JobEDocRestService,
              private jobSharedDocRest: JobSharedDocRestService,
              private letterRest: LetterRestService,
              private jobChangeOrderRestService: JobChangeOrderRestService,
              private insuranceRestService: JobInsuranceRestService,
              private communicationRestService: JobCommunicationRestService,
              private communicationService: CommunicationRestService,
              private jobDrawsRest: JobDrawsRestService,
              private report: ReportService) {
  }

  initDrawsTabView(route: ActivatedRouteSnapshot): any {
    const jobId = getParentJobId(route);
    this.jobId = jobId;
    return {
      jobId,
      draws: this.getDraws(jobId),
      invoiceGridLayout: this.gridLayoutService.get('jobDetailsDrawsInvoices'),
      paymentGridLayout: this.gridLayoutService.get('jobDetailsDrawsPayments'),
      paymentScheduleGridLayout: this.gridLayoutService.get('jobDetailsDrawsPaymentSchedule'),
      lendersScheduleIncludedGridLayout: this.gridLayoutService.get('jobDetailsDrawsLendersScheduleIncluded'),
      lendersScheduleOverheadsGridLayout: this.gridLayoutService.get('jobDetailsDrawsLendersScheduleOverheads'),
      invoiceTypeDict: this.dictionaryService.getByEnum(InvoiceType),
      reportUrl: this.report.generateUrl(REPORT_JOB_LENDER_S_SCHEDULE, [reportIdField('JobId', jobId)]),
    };
  }

  getBasicInfo(): Observable<JobBasicInfo> {
    return this.jobRest.getBasic(this.jobId);
  }

  sendEmail(email: CommunicationListItem): Observable<CommunicationListItem> {
    return this.communicationRestService.sendEmail(this.jobId, email);
  }

  getDraws(jobId: ID): Observable<JobDrawsDetails> {
    return this.jobDrawsRest.getMaster(jobId).pipe(map$(jobDrawsDetailsFromDto));
  }

  recalculateDraws(jobId: ID, request: JobDrawsRecalculateRequest, newDrawRuleId?: ID): Observable<JobDrawsDetails> {
    return this.jobDrawsRest.recalculateMaster(jobId, request, newDrawRuleId).pipe(map$(jobDrawsDetailsFromDto));
  }

  refreshDraws(jobId: ID,
               lendersScheduleDrawRuleId: ID,
               request: JobDrawsRecalculateRequest): Observable<JobDrawsDetails> {
    return this.jobDrawsRest.refreshMaster(jobId,
                                           lendersScheduleDrawRuleId,
                                           request).pipe(map$(jobDrawsDetailsFromDto));
  }

  changeClient(jobId: ID, version: EntityVersion, contactId: ID, originalContactId: ID): Observable<JobBasicInfo> {
    return this.jobRest.changeClient(jobId, {
      version,
      contactId,
      originalContactId,
    });
  }

  findSalesPersonsByName(query: FilteredDataQuery<string>): Observable<JobSalesPerson[]> {
    return this.userRestService.findAllAsDataSource({
       name: query.filter,
       showInactive: false,
       autocomplete: true,
       systemRoles: systemRolesBitSet(SystemRoleBit.SalesPerson),
     }).select(query).pipe(map$(getSlicedData));
  }

  reassign(jobId: ID, version: EntityVersion, salesPersonId: ID): Observable<void> {
    return this.jobRest.reassign(jobId, version, salesPersonId);
  }
}
